import {
  HappycoUnitFragment,
  HappycoUnitsQuery,
  HappycoUnitsQueryVariables,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select, SingleValue } from "chakra-react-select";
import { SystemStyleObject } from "@chakra-ui/react";
import colors from "app/colors";
import HAPPYCO_UNITS_QUERY from "./_networking/happyco-units";

interface IProps {
  propertyId?: string;
  onUnitSelected: (happycoUnit?: SingleValue<HappycoUnitFragment>) => void;
}

const HappycoUnitSelector = ({ propertyId, onUnitSelected }: IProps) => {
  const { data } = useQuery<HappycoUnitsQuery, HappycoUnitsQueryVariables>(
    HAPPYCO_UNITS_QUERY,
    {
      variables: propertyId ? { propertyId } : undefined,
      skip: !propertyId,
    },
  );

  const [value, setValue] =
    React.useState<SingleValue<HappycoUnitFragment> | null>();

  useEffect(() => {
    setValue(null);
    onUnitSelected(undefined);
  }, [onUnitSelected, propertyId]);

  const onChange = (happycoUnit?: SingleValue<HappycoUnitFragment>) => {
    setValue(happycoUnit);
    onUnitSelected(happycoUnit);
  };

  const getOptionLabel = useCallback(
    (happycoUnit: HappycoUnitFragment) => happycoUnit.name,
    [],
  );

  const getOptionValue = useCallback(
    (happycoUnit: HappycoUnitFragment) => happycoUnit.happycoId,
    [],
  );

  const happycoUnits = useMemo(() => data?.happycoUnits || [], [data]);

  return (
    <Select
      options={happycoUnits}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder="Select unit or area"
      noOptionsMessage={() =>
        "No units or areas found for the selected property"
      }
      onChange={onChange}
      value={value}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
          fontSize: 14,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        control: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          minHeight: "34px",
        }),
      }}
    />
  );
};

export default memo(HappycoUnitSelector);
